import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState,
  type RowSelectionState,
  type ColumnDef,
} from "@tanstack/react-table"
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import {
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  DollarSign,
  PenTool,
  AlertCircle,
  Info,
  Loader2,
  FileSpreadsheet,
  CheckCircle,
  Filter,
} from "lucide-react"
import useTranslator from "@pma/hooks/use-translator"
import { DataTableColumnHeader } from "@components/common/data-table/data-table-column-header"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import type { DictType } from "@pma/types"
import { buildBoQHierarchicalData, cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { Checkbox } from "@components/common/checkbox"
import RequestAdditionalAmount from "./request-additional-amount"
import ApproveBoqItems from "./approve-boq-items"
import { toast } from "@pma/hooks/use-toast"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  BILL_APPROVE_ENDPOINT,
  EDIT_BILL_ENDPOINT,
  SET_BILL_VAT_ENDPOINT,
  FINANCIAL_SERVER_URL,
  GET_MAIN_BOQ_ENDPOINT,
} from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import AccumulatedBillAmounts from "./accumulated-bill-amounts"
import { Badge } from "../common/badge"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import { Switch } from "@components/common/switch"
import PmsLoader from "@components/common/loader"
import type { BoQItem } from "./main-boq-items"

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

type BoQEditType = "contractor" | "approval"

interface BoQEditProps {
  boqId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  currentBill?: any
  showStatus?: boolean
  showCurrentBill?: boolean
  type: any
}

interface BillAmountInputProps {
  item: DictType
  value: number
  disabled?: boolean
  isActive: boolean
  onFocus: () => void
  onChange: (itemId: string, value: number, raw: string) => void
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

/** Build a Map<boq_item_id, summed_amount> from bill items */
const buildBillItemMap = (billItems?: any[]): Map<number, number> => {
  const map = new Map<number, number>()
  billItems?.forEach((bill: any) => {
    map.set(bill.boq_item_id, (map.get(bill.boq_item_id) || 0) + bill.amount)
  })
  return map
}

/** Build a Map<boq_item_id, { amount, approved_amount }> for supervisor-reduced detection */
const buildSupervisorReducedMap = (
  billItems?: any[],
): Map<number, { amount: number; approved_amount: number }> => {
  const map = new Map<number, { amount: number; approved_amount: number }>()
  billItems?.forEach((bi: any) => {
    if (
      bi.approved_amount != null &&
      bi.amount != null &&
      bi.approved_amount < bi.amount &&
      bi.approved_amount > 0
    ) {
      map.set(bi.boq_item_id, { amount: bi.amount, approved_amount: bi.approved_amount })
    }
  })
  return map
}

const showToast = (title: string, message: string, variant: "success" | "error" | "info") => {
  toast({
    title,
    description: message,
    variant,
  })
}

// ─────────────────────────────────────────────────────────────
// Custom Hooks
// ─────────────────────────────────────────────────────────────

function useBoQData(boqId: string, open: boolean, currentBill?: any) {
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const {
    data: mainBoQDetails,
    isLoading,
    refetch,
    isError,
  } = useQuery({
    queryKey: ["main-boq", boqId, open],
    queryFn: async () => {
      const { data } = await axios.post(
        FINANCIAL_SERVER_URL + GET_MAIN_BOQ_ENDPOINT,
        { boq_id: boqId },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      )
      return data
    },
    enabled: open,
  })

  const boqItems: BoQItem[] = useMemo(() => {
    const items = mainBoQDetails?.boq_items || []
    if (!currentBill?._bill_items?.length) return items

    const billItemMap = buildBillItemMap(currentBill._bill_items)
    return items.map((item: any) =>
      billItemMap.has(item.id) ? { ...item, amount: billItemMap.get(item.id) } : item,
    )
  }, [mainBoQDetails?.boq_items, currentBill?.id])

  return { mainBoQDetails, boqItems, isLoading, refetch, isError }
}

function useHierarchicalTree(boqItems: BoQItem[], showStatus: boolean) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set(""))

  const hierarchicalData = useMemo(() => buildBoQHierarchicalData(boqItems), [boqItems])

  const visibleData = useMemo(
    () =>
      hierarchicalData.filter((item) => {
        if (item.level === 0) return true
        const codeParts = item?.number.split(".")
        for (let i = 1; i <= item.level; i++) {
          if (!expandedRows.has(codeParts.slice(0, i).join("."))) return false
        }
        return true
      }),
    [hierarchicalData, expandedRows],
  )

  useEffect(() => {
    setExpandedRows(new Set(hierarchicalData.map((item) => item.number)))
  }, [showStatus, hierarchicalData])

  const toggleExpanded = useCallback(
    (number: string) => {
      setExpandedRows((prev) => {
        const next = new Set(prev)
        if (next.has(number)) {
          next.delete(number)
          hierarchicalData.forEach((item) => {
            if (item?.number?.startsWith(number + ".")) next.delete(item.number)
          })
        } else {
          next.add(number)
        }
        return next
      })
    },
    [hierarchicalData],
  )

  return { hierarchicalData, visibleData, expandedRows, toggleExpanded }
}

function useBillSubmission(
  type: BoQEditType,
  boqItems: BoQItem[],
  currentBill: any,
  billAmounts: Record<string, number>,
  discountAmounts: Record<string, number>,
  declinedItems: Record<string, boolean>,
  billMessageText: string,
  finishApproval: boolean,
  retentionPercent: number,
  vatPercent: number,
  onSuccess: () => void,
) {
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const isApproval = type === "approval"

  const submitBill = async () => {
    const existingBillItemIds = new Set<number>()

    // Build items from currentBill._bill_items directly
    const items: any[] = []
    currentBill?._bill_items?.forEach((billItem: any) => {
      existingBillItemIds.add(billItem.boq_item_id)
      const amount = Number(billAmounts[billItem.boq_item_id]) || 0
      const shouldInclude = isApproval
        ? amount > 0 || declinedItems[billItem.boq_item_id]
        : amount > 0
      if (!shouldInclude) return

      items.push({
        bill_item_id: billItem.id,
        amount,
        discount:
          discountAmounts[billItem?.boq_item_id] == 0
            ? discountAmounts[billItem?.boq_item_id]
            : null,
        message_text: "",
        ...(isApproval ? { declined: !!declinedItems[billItem.boq_item_id] } : { delete: false }),
      })
    })

    // Build new_items from boqItems that are NOT in currentBill
    const newItems: any[] = []
    boqItems.forEach((item: any) => {
      if (!item?.is_item || existingBillItemIds.has(item.id)) return
      const amount = Number(billAmounts[item.id]) || 0
      if (amount <= 0) return
      newItems.push({
        boq_item_id: item.id,
        amount,
        discount:
          discountAmounts[item?.id] == 0 ? discountAmounts[item?.id] : null,
        message_text: "",
      })
    })

    const billId = currentBill?._id || currentBill?.id
    const endpoint = isApproval ? BILL_APPROVE_ENDPOINT : EDIT_BILL_ENDPOINT
    const payload = isApproval
      ? {
          bill_id: billId,
          bill_message_text: billMessageText,
          finish_approval: finishApproval,
          retention: retentionPercent,    // approve endpoint field name is "retention" (saves as retention_percent)
          vat_percent: vatPercent,
          items,
          new_items: newItems,
        }
      : { bill_id: billId, to_approve: false, retention_percent: retentionPercent, vat_percent: vatPercent, items, new_items: newItems }

    const { data } = await axios.post(FINANCIAL_SERVER_URL + endpoint, payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    // Update VAT if it changed and this is an edit (not approval)
    if (!isApproval && vatPercent !== (currentBill?.vat_percent ?? 18)) {
      await axios.post(
        FINANCIAL_SERVER_URL + SET_BILL_VAT_ENDPOINT,
        { bill_id: billId, vat: vatPercent },
        { headers: { Authorization: `Bearer ${accessToken}` } },
      )
    }

    return data
  }

  return useMutation({
    mutationKey: [isApproval ? "approve-bill" : "edit-bill"],
    mutationFn: submitBill,
    onSuccess: () => {
      showToast(
        translate("Success"),
        translate(isApproval ? "bill approved successfully" : "bill edited successfully") + " — " + translate("moved to supervisor approval"),
        "success",
      )
      onSuccess()
    },
    onError: (error: DictType) => {
      showToast(
        translate("Error"),
        getFinancialErrorMessage(error, isApproval ? "approve bill" : "edit bill", translate),
        "error",
      )
    },
  })
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

function BillAmountInput({
  item,
  value,
  disabled = false,
  isActive,
  onFocus,
  onChange,
}: BillAmountInputProps) {
  const { translate } = useTranslator()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isActive) inputRef.current?.focus()
  }, [isActive])

  return (
    <Input
      ref={inputRef}
      type='number'
      min={0}
      disabled={disabled}
      className={cn("w-full border-primary sm:w-[10ch]", disabled && "opacity-50")}
      value={value}
      onFocus={onFocus}
      onChange={(e) => {
        const numValue = Number(e.target.value)
        if (numValue > item?.amount) {
          showToast("Info", translate("Bill item amount is more than tender amount"), "info")
        }
        onChange(item?.id, numValue, e.target.value)
      }}
    />
  )
}

function BillStatusBadge({ bill, translate }: { bill: any; translate: (key: string) => string }) {
  if (bill?.amount_by_signer) {
    return (
      <Badge variant='secondary' className='flex min-w-[100px] justify-center gap-1'>
        <PenTool className='h-3 w-3' /> {translate("Signed")}
      </Badge>
    )
  }

  const isDeclined =
    bill?.billStatus === "declined" ||
    (bill?.billStatus === "paid" && bill?.amount_by_signer < 1) ||
    bill?.is_declined ||
    bill?.declined

  if (isDeclined) {
    return (
      <Badge variant='destructive' className='flex min-w-[100px] justify-center gap-1'>
        <AlertCircle className='h-3 w-3' /> {translate("Declined")}
      </Badge>
    )
  }

  if (bill?.approved_amount >= bill?.amount) {
    return (
      <Badge variant='secondary' className='flex min-w-[100px] justify-center'>
        {translate("Approved")}
      </Badge>
    )
  }

  if (bill?.approved_amount > 0) {
    return (
      <Badge variant='default' className='flex min-w-[100px] justify-center gap-1'>
        <Info className='h-3 w-3' /> {translate("Partially Approved")}
      </Badge>
    )
  }

  return (
    <Badge variant='disabled' className='flex min-w-[100px] justify-center gap-1'>
      <Loader2 className='h-3 w-3 animate-spin' /> {translate("Pending")}
    </Badge>
  )
}

/** Displays requested/approved amounts beneath the bill input */
function BillAmountDetails({
  item,
  showStatus,
  showCurrentBill,
  translate,
}: {
  item: DictType
  showStatus: boolean
  showCurrentBill: boolean
  translate: (key: string) => string
}) {
  // Determine which values to show
  const hasInlineBill = item?.is_item && item?.bill && !showStatus && !showCurrentBill

  if (!hasInlineBill && !showStatus && !showCurrentBill) return null

  const requested = showCurrentBill ? item?.bill?.amount || item?.amount || 0 : item?.bill?.amount
  const approved = showCurrentBill
    ? item?.bill?.approved_amount || item?.paid || 0
    : item?.bill?.approved_amount

  return (
    <>
      <span className='text-xs text-primary'>
        {translate("Requested")}: {requested}
      </span>
      <span className='text-xs text-green-600'>
        {translate("Bill Approved")}: {approved}
      </span>
    </>
  )
}

/** Current Sum cell — handles showStatus / showCurrentBill / default modes */
function CurrentSumCell({
  item,
  showStatus,
  showCurrentBill,
  billAmounts,
  isHebrew,
  translate,
}: {
  item: DictType
  showStatus: boolean
  showCurrentBill: boolean
  billAmounts: Record<string, number>
  isHebrew: boolean
  translate: (key: string) => string
}) {
  if (showStatus) {
    return (
      <div className='flex flex-col items-center gap-1'>
        <span className='text-xs'>
          {translate("Requested")}:{" "}
          {formatCurrency(item?.discount_price * item?.bill?.amount, isHebrew)}
        </span>
        <span className='text-xs text-green-600'>
          {translate(item?.bill?.is_signed ? "Signed" : "Approved")}:{" "}
          {formatCurrency(item?.discount_price * item?.bill?.approved_amount, isHebrew)}
        </span>
      </div>
    )
  }

  if (showCurrentBill) {
    const requestedSum =
      item?.requested_sum ?? item?.discount_price * (item?.bill?.amount || item?.amount || 0)
    const approvedSum =
      item?.approved_sum ?? item?.discount_price * (item?.bill?.approved_amount || item?.paid || 0)

    return (
      <div className='flex flex-col items-center gap-1'>
        <span className='text-xs'>
          {translate("Requested")}: {formatCurrency(requestedSum, isHebrew)}
        </span>
        <span className='text-xs text-green-600'>
          {translate(item?.is_signed ? "Signed" : "Approved")}:{" "}
          {formatCurrency(approvedSum, isHebrew)}
        </span>
      </div>
    )
  }

  if (!item?.is_item) return null

  return <AccumulatedTotalCell currentSum preview item={item} billAmounts={billAmounts} />
}

function AccumulatedTotalCell({
  item,
  billAmounts,
  currentSum,
  preview,
}: {
  item: DictType
  billAmounts: Record<string, number>
  currentSum?: boolean
  preview?: boolean
}) {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"

  const billAmount = billAmounts[item?.id] || 0
  const totalAccumulated = billAmount + (item?.paid || 0)
  const exceeded = totalAccumulated > item?.amount

  if (currentSum) {
    const displayAmount = preview
      ? billAmount * (item?.discount_price || 0)
      : item?.bill
        ? item.discount_price * item.bill.amount
        : 0

    return (
      <div className='flex flex-col items-center'>
        <span
          className={cn("text-slate-500", billAmount && "text-primary", exceeded && "text-red-500")}
        >
          {formatCurrency(displayAmount, isHebrew)}
        </span>
      </div>
    )
  }

  return (
    <div className='flex flex-col items-center'>
      <span className={cn(exceeded ? "text-red-500" : "text-green-500")}>
        {formatCurrency(item?.discount_price * totalAccumulated, isHebrew)}
      </span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

export default function BoQEdit({
  boqId,
  open,
  type,
  onOpenChange,
  currentBill,
  showStatus = false,
  showCurrentBill = false,
}: BoQEditProps) {
  const { translate, locale } = useTranslator()
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()

  const isHebrew = locale === "he"
  const isApproval = type === "approval"
  const dir = isHebrew ? "rtl" : "ltr"
  const borderCls = isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0"

  // ── Data ───────────────────────────────────────────────────

  const { mainBoQDetails, boqItems, isLoading, refetch, isError } = useBoQData(
    boqId,
    open,
    currentBill,
  )

  useEffect(() => {
    if (!isError) return
    const params = new URLSearchParams(searchParam)
    params.delete("main")
    navigate(`?${params.toString()}`)
  }, [isError])

  // ── Tree ───────────────────────────────────────────────────

  const { hierarchicalData, visibleData, expandedRows, toggleExpanded } = useHierarchicalTree(
    boqItems,
    showStatus,
  )

  // ── State ──────────────────────────────────────────────────

  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [billAmounts, setBillAmounts] = useState<Record<string, number>>({})
  const [activeInputId, setActiveInputId] = useState<string | null>(null)
  const [discountAmounts, setDiscountAmounts] = useState<Record<string, number>>({})
  const [excludeDiscountEnabled, setExcludeDiscountEnabled] = useState<Record<string, boolean>>({})
  const [activeDiscountInputId, setActiveDiscountInputId] = useState<string | null>(null)
  const [declinedItems, setDeclinedItems] = useState<Record<string, boolean>>({})
  const [finishApproval, setFinishApproval] = useState(false)
  const [billMessageText, setBillMessageText] = useState("")
  const [retentionPercent, setRetentionPercent] = useState<number>(currentBill?.retention_percent ?? 0)
  const [vatPercent, setVatPercent] = useState<number>(currentBill?.vat_percent ?? 18)
  const [searchQuery, setSearchQuery] = useState("")

  // Supervisor-reduced highlighting + flagged-only filter
  const [showOnlyFlagged, setShowOnlyFlagged] = useState(false)

  const supervisorReducedMap = useMemo(
    () => buildSupervisorReducedMap(currentBill?._bill_items),
    [currentBill],
  )

  // Sub-dialogs
  const [showApproveDialog, setShowApproveDialog] = useState(false)
  const [showAdditionalAmountDialog, setShowAdditionalAmountDialog] = useState(false)
  const [selectedItemForAdditional, setSelectedItemForAdditional] = useState<DictType>({})
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false)

  // ── Re-sync retentionPercent / vatPercent when currentBill changes ──────
  useEffect(() => {
    setRetentionPercent(currentBill?.retention_percent ?? 0)
    setVatPercent(currentBill?.vat_percent ?? 18)
  }, [currentBill?.id])

  // ── Pre-populate amounts from currentBill ──────────────────

  useEffect(() => {
    if (!currentBill || !boqItems.length) return
    const billItemMap = buildBillItemMap(currentBill._bill_items)
    const initial: Record<string, number> = {}
    const initialForSwitch: Record<string, boolean> = {}
    const initialForDiscount: Record<string, number> = {}

    boqItems.forEach((item: any) => {
      if (item?.is_item && billItemMap.has(item.id)) {
        initial[item.id] = billItemMap.get(item.id)!
      }
    })
    if (Object.keys(initial).length > 0) setBillAmounts(initial)

    currentBill._bill_items?.forEach((billItem: any) => {
      if (billItem?._boq_item?.is_item && billItem?.item_discount == 0) {
        initialForSwitch[billItem.boq_item_id] = true
        initialForDiscount[billItem.boq_item_id] = 0
      }
    })

    if (Object.keys(initialForSwitch).length > 0 && Object.keys(initialForDiscount).length > 0) {
      setExcludeDiscountEnabled(initialForSwitch)
      setDiscountAmounts(initialForDiscount)
    }
  }, [currentBill?.id, boqItems])

  // ── Handlers ───────────────────────────────────────────────

  const handleRowSelection = useCallback(
    (rowId: string, isSelected: boolean) => {
      const item = hierarchicalData.find((i) => i.id == rowId)
      if (!item) return

      setRowSelection((prev) => {
        const next = { ...prev }
        const descendants = hierarchicalData.filter((c) => c?.number.startsWith(item.number + "."))

        if (isSelected) {
          next[rowId] = true
          descendants.forEach((c) => {
            next[c.id] = true
          })
        } else {
          delete next[rowId]
          descendants.forEach((c) => {
            delete next[c.id]
          })

          if (item.parentCode) {
            const siblings = hierarchicalData.filter((s) => s.parentCode === item.parentCode)
            if (!siblings.some((s) => next[s.id])) {
              const parent = hierarchicalData.find((p) => p?.number === item.parentCode)
              if (parent) delete next[parent.id]
            }
          }
        }
        return next
      })
    },
    [hierarchicalData],
  )

  const handleBillAmountChange = useCallback((itemId: string, value: number, raw: string) => {
    setBillAmounts((prev) => {
      if (value === 0 || raw === "") {
        const { [itemId]: _, ...rest } = prev
        return rest
      }
      return { ...prev, [itemId]: value }
    })
    setActiveInputId(itemId)
  }, [])

  const toggleDeclined = useCallback((itemId: string) => {
    setDeclinedItems((prev) => {
      const next = { ...prev }
      next[itemId] ? delete next[itemId] : (next[itemId] = true)
      return next
    })
  }, [])

  // ── Submission ─────────────────────────────────────────────

  const mutation = useBillSubmission(
    type,
    boqItems,
    currentBill,
    billAmounts,
    discountAmounts,
    declinedItems,
    billMessageText,
    finishApproval,
    retentionPercent,
    vatPercent,
    () => {
      const params = new URLSearchParams(searchParam)
      params.set("revalidate", "bills")
      params.set("bill_tab", "unsigned")
      navigate(`?${params.toString()}`)
      refetch()
      onOpenChange(false)
    },
  )

  // Filtered data for "show only flagged items" toggle
  const filteredVisibleData = useMemo(() => {
    if (!showOnlyFlagged) return visibleData
    const flaggedIds = new Set(supervisorReducedMap.keys())
    const flaggedNumbers = new Set<string>()
    visibleData.forEach((item) => {
      if (flaggedIds.has(item.id)) {
        flaggedNumbers.add(item.number)
        const parts = item.number.split(".")
        for (let i = 1; i < parts.length; i++) {
          flaggedNumbers.add(parts.slice(0, i).join("."))
        }
      }
    })
    return visibleData.filter((item) => flaggedNumbers.has(item.number))
  }, [visibleData, showOnlyFlagged, supervisorReducedMap])

  // Search filter
  const searchFilteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return filteredVisibleData
    const matchingNumbers = new Set<string>()
    filteredVisibleData.forEach((item) => {
      if (item.description?.toLowerCase().includes(q) || item.number?.toLowerCase().includes(q)) {
        matchingNumbers.add(item.number)
        const parts = item.number.split(".")
        for (let i = 1; i < parts.length; i++) {
          matchingNumbers.add(parts.slice(0, i).join("."))
        }
      }
    })
    return filteredVisibleData.filter((item) => matchingNumbers.has(item.number))
  }, [filteredVisibleData, searchQuery])

  const flaggedCount = supervisorReducedMap.size

  const submissionSummary = useMemo(() => {
    const entries = Object.entries(billAmounts).filter(([_, v]) => v > 0)
    const itemCount = entries.length
    const totalSum = entries.reduce((sum, [id, qty]) => {
      const item = boqItems.find((b: any) => String(b.id) === id)
      return sum + qty * (item?.discount_price || 0)
    }, 0)
    return { itemCount, totalSum }
  }, [billAmounts, boqItems])

  const isSubmitDisabled =
    boqItems.length < 1 || mutation.isPending || Object.keys(billAmounts).length < 1

  // ── Column definitions ─────────────────────────────────────

  const columns = useMemo(
    (): ColumnDef<any>[] => [
      // Select
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            disabled
            onCheckedChange={() => {}}
            aria-label='Select all'
            className='mr-2'
          />
        ),
        cell: ({ row }) =>
          row.original?.is_item ? (
            <Checkbox
              checked={!!rowSelection[row.original.id]}
              onCheckedChange={(v) => handleRowSelection(row.original.id, !!v)}
              aria-label='Select row'
              disabled
              className='mr-2'
            />
          ) : null,
        size: 40,
      },
      // Section number
      {
        accessorKey: "number",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Section')} />,
        cell: ({ row }: any) => {
          const { number: itemNumber, hasChildren, parentCode } = row.original
          const indent = String(parentCode || "1").split(".").length * 20

          return (
            <div
              className='flex gap-1'
              style={{ [isHebrew ? "paddingRight" : "paddingLeft"]: `${indent}px` }}
            >
              {hasChildren && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-5 w-5 p-0'
                  onClick={() => toggleExpanded(itemNumber)}
                >
                  {expandedRows.has(itemNumber) ? (
                    <ChevronDown className='h-4 w-4' />
                  ) : (
                    <ChevronRight className='h-4 w-4' />
                  )}
                </Button>
              )}
              <span className='text-xs sm:text-sm'>{itemNumber}</span>
            </div>
          )
        },
        enableSorting: false,
        size: 100,
      },
      // Description
      {
        accessorKey: "description",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Description')} />,
        cell: ({ row }: any) => {
          const reduced = supervisorReducedMap.get(row.original?.id)
          return (
            <div className='max-w-[200px] text-right text-xs leading-tight sm:max-w-xs sm:text-sm lg:min-w-[370px]'>
              {row.original?.description}
              {reduced && (
                <div className='mt-0.5 flex items-center gap-1'>
                  <Badge variant='outline' className='border-amber-400 bg-amber-50 text-amber-700 text-[10px] px-1.5 py-0'>
                    {isHebrew ? "מפקח הפחית" : "Supervisor reduced"}: {reduced.approved_amount}
                  </Badge>
                </div>
              )}
            </div>
          )
        },
        size: 300,
        enableSorting: false,
      },
      // Status (conditional)
      ...(showStatus
        ? [
            {
              accessorKey: "status",
              header: ({ column }: any) => <DataTableColumnHeader column={column} title={translate('Status')} />,
              cell: ({ row }: any) => (
                <BillStatusBadge bill={row.original.bill} translate={translate} />
              ),
            } as ColumnDef<any>,
          ]
        : []),
      // Unit
      {
        accessorKey: "units",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Unit')} className='text-center' />
        ),
        cell: ({ row }: any) => (
          <div className='text-center text-xs sm:text-sm'>{row.original?.units}</div>
        ),
        enableSorting: false,
      },
      // Price per unit
      {
        accessorKey: "boq_cost",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Price per unit')} />,
        cell: ({ row }: any) => {
          const item = row.original
          if (!item?.is_item) return <div />
          return (
            <div className='min-w-fit text-center text-xs sm:text-sm'>
              <Tooltip>
                <TooltipTrigger>
                  <span className='flex items-center gap-1'>
                    {formatCurrency(item.discount_price, isHebrew)}
                    <small className='text-[10px]'>
                      ({Number(((item?.total_discount ?? 0) * 100).toFixed(2))}%)
                    </small>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {translate("Discount applied to price")}:{" "}
                  {Number(((item?.total_discount ?? 0) * 100).toFixed(2))}%
                </TooltipContent>
              </Tooltip>
            </div>
          )
        },
        size: 150,
        enableSorting: false,
      },
      {
        accessorKey: "discount_price",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Exclude Discount')} />,
        cell: ({ row }: any) => {
          const item = row.original
          return (
            <div className='flex min-w-[120px] flex-col items-center gap-1 text-center text-xs sm:min-w-fit sm:text-sm'>
              {item?.is_item && (
                <Switch
                  checked={!!excludeDiscountEnabled[item?.id]}
                  onCheckedChange={(checked) => {
                    setExcludeDiscountEnabled((prev) => ({ ...prev, [item?.id]: !!checked }))
                    if (checked) {
                      setDiscountAmounts((prev) => ({ ...prev, [item?.id]: 0 }))
                    } else {
                      setDiscountAmounts((prev) => {
                        const { [item?.id]: _, ...rest } = prev
                        return rest
                      })
                    }
                  }}
                />
              )}
            </div>
          )
        },
        size: 100,
        enableSorting: false,
        enableHiding: true,
      },
      // Amount from estimate
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Amount from estimate')} />
        ),
        cell: ({ row }: any) => (
          <div className='max-w-xs text-right text-xs leading-tight sm:text-sm'>
            {row.original?.amount || ""}
          </div>
        ),
        size: 100,
        enableSorting: false,
      },
      // Accumulated Amount
      {
        accessorKey: "paid",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Amount')} />
        ),
        cell: ({ row }: any) => (
          <div className='text-center text-xs sm:text-sm'>
            {row.original?.is_item ? (
              <AccumulatedBillAmounts bill={row.original?._boq_item}>
                {row.original.paid}
              </AccumulatedBillAmounts>
            ) : null}
          </div>
        ),
        size: 50,
        enableSorting: false,
      },
      // Current Bill / Approved Amount
      {
        accessorKey: "current_bill",
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={isApproval ? "Approved Amount" : "Current Bill"}
          />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          return (
            <div className='flex min-w-[120px] flex-col text-center text-xs sm:min-w-fit sm:text-sm'>
              {item?.is_item && (
                <div className='flex items-center gap-1'>
                  <BillAmountInput
                    item={item}
                    value={billAmounts[item.id] ?? 0}
                    isActive={activeInputId === item.id}
                    onFocus={() => setActiveInputId(item.id)}
                    onChange={handleBillAmountChange}
                    disabled={isApproval && declinedItems[item.id]}
                  />
                  {isApproval && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={declinedItems[item.id] ? "destructive" : "outline"}
                          size='sm'
                          className='h-8 w-8 flex-shrink-0 p-0'
                          onClick={() => toggleDeclined(item.id)}
                        >
                          <AlertCircle className='h-3.5 w-3.5' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {translate(
                          declinedItems[item.id]
                            ? "Item declined - click to undo"
                            : "Decline this item",
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              )}
              <BillAmountDetails
                item={item}
                showStatus={showStatus}
                showCurrentBill={showCurrentBill}
                translate={translate}
              />
            </div>
          )
        },
        size: 100,
        enableSorting: false,
        enableHiding: true,
      },
      // Current Sum
      {
        accessorKey: "current_sum",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Current Sum')} />,
        cell: ({ row }: any) => (
          <div className='text-center text-xs font-bold sm:text-sm'>
            <CurrentSumCell
              item={row.original}
              showStatus={showStatus}
              showCurrentBill={showCurrentBill}
              billAmounts={billAmounts}
              isHebrew={isHebrew}
              translate={translate}
            />
          </div>
        ),
        size: 200,
        enableSorting: false,
      },
      // BoQ Sum
      {
        accessorKey: "estimated_sum",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Boq sum')} />,
        cell: ({ row }: any) => {
          const item = row.original
          if (!item?.is_item) return <div />
          return (
            <div className='text-center text-xs font-bold sm:text-sm'>
              {formatCurrency(row.original?.real_sum, isHebrew)}
            </div>
          )
        },
        enableSorting: false,
      },
      // Accumulated Sum
      {
        accessorKey: "sum",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Accumulated Sum')} />,
        cell: ({ row }: any) => {
          const item = row.original
          if (!item?.is_item) return <div />
          return (
            <div className='text-center text-xs font-bold sm:text-sm'>
              <span
                className={cn("text-slate-500", billAmounts[row.original?.id] && "text-primary")}
              >
                {formatCurrency(Number(row.original?.paid_sum) || 0, isHebrew)}
              </span>
            </div>
          )
        },
        enableSorting: false,
      },
    ],
    [
      expandedRows,
      rowSelection,
      hierarchicalData,
      billAmounts,
      discountAmounts,
      excludeDiscountEnabled,
      isHebrew,
      activeInputId,
      activeDiscountInputId,
      showCurrentBill,
      showStatus,
      isApproval,
      declinedItems,
      handleRowSelection,
      handleBillAmountChange,
      toggleDeclined,
      toggleExpanded,
      translate,
    ],
  )

  // ── Table instance ─────────────────────────────────────────

  const table = useReactTable({
    data: searchFilteredData,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: true,
  })

  // ── Render ─────────────────────────────────────────────────

  const SubmitIcon = isApproval ? CheckCircle : DollarSign

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-[calc(100vh-2rem)] max-h-[95vh] w-full max-w-[91vw] flex-col overflow-hidden p-4 sm:h-[calc(100vh-4rem)] sm:p-6",
          dir === "rtl" ? "rtl" : "ltr",
        )}
      >
        {/* Header */}
        <DialogHeader className='mb-4 flex flex-shrink-0 flex-row items-center justify-between space-y-0'>
          <div className='min-w-0 flex-1'>
            <DialogTitle className='flex items-center gap-2 text-base sm:text-lg'>
              <FileSpreadsheet className='h-4 w-4 flex-shrink-0 text-primary sm:h-5 sm:w-5' />
              <span className='truncate'>{mainBoQDetails?.boq?.name}</span>
            </DialogTitle>
            <DialogDescription
              className={cn("mt-1 text-xs sm:text-sm", isHebrew ? "text-right" : "text-left")}
            >
              {translate(
                isApproval
                  ? "Review and approve quantities for each item"
                  : "Choose quantities for each item to edit the bill",
              )}
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Scrollable content */}
        <div className='-mx-4 min-h-0 flex-1 overflow-y-auto px-2 sm:-mx-6 sm:px-4'>
          {isLoading ? (
            <div className='flex h-full items-center justify-center'>
              <PmsLoader />
            </div>
          ) : (
            <div className='w-full overflow-x-hidden' dir={dir}>
              {/* Approval message */}
              {isApproval && (
                <div className='mb-4 flex flex-col gap-2'>
                  <label className='text-xs font-medium text-gray-700 sm:text-sm'>
                    {translate("Approval Message")}
                  </label>
                  <Input
                    placeholder={translate("Add a message for this approval")}
                    value={billMessageText}
                    onChange={(e) => setBillMessageText(e.target.value)}
                  />
                </div>
              )}

              {/* Retention % and VAT % — visible for both contractor and approval */}
              <div className='mb-4 flex flex-wrap gap-4'>
                <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2'>
                  <label
                    htmlFor='retention-percent-input'
                    className='text-xs font-medium text-gray-700 sm:text-sm sm:whitespace-nowrap'
                  >
                    {isHebrew ? "עיכבון %" : "Retention %"}
                  </label>
                  <Input
                    id='retention-percent-input'
                    type='number'
                    min={0}
                    max={100}
                    step={0.1}
                    className='w-24'
                    value={retentionPercent}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value)
                      setRetentionPercent(isNaN(val) ? 0 : Math.max(0, Math.min(100, val)))
                    }}
                    placeholder='0'
                  />
                </div>
                <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2'>
                  <label
                    htmlFor='vat-percent-input'
                    className='text-xs font-medium text-gray-700 sm:text-sm sm:whitespace-nowrap'
                  >
                    {isHebrew ? 'מע"מ %' : "VAT %"}
                  </label>
                  <Input
                    id='vat-percent-input'
                    type='number'
                    min={0}
                    max={100}
                    step={0.1}
                    className='w-24'
                    value={vatPercent}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value)
                      setVatPercent(isNaN(val) ? 18 : Math.max(0, Math.min(100, val)))
                    }}
                    placeholder='18'
                  />
                </div>
              </div>

              {/* Search */}
              <div className='mb-3 flex items-center gap-2'>
                <Input
                  placeholder={isHebrew ? "חיפוש פריט..." : "Search item..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='h-8 max-w-xs text-xs'
                />
                {searchQuery && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-8 px-2 text-xs text-muted-foreground'
                    onClick={() => setSearchQuery("")}
                  >
                    ✕
                  </Button>
                )}
              </div>

              {/* Filter: show only flagged items */}
              {flaggedCount > 0 && (
                <div className='mb-3 flex items-center gap-2'>
                  <Button
                    variant={showOnlyFlagged ? "default" : "outline"}
                    size='sm'
                    className='h-8 gap-1.5 text-xs'
                    onClick={() => setShowOnlyFlagged((v) => !v)}
                  >
                    <Filter className='h-3.5 w-3.5' />
                    {isHebrew ? "הצג רק פריטים שהשתנו" : "Show only changed items"}
                    <Badge variant='secondary' className='mr-1 ml-1 h-5 min-w-[20px] px-1.5 text-[10px]'>
                      {flaggedCount}
                    </Badge>
                  </Button>
                </div>
              )}

              {/* Table */}
              <div className='overflow-x-auto rounded-md border'>
                <Table className='text-xs sm:text-sm'>
                  <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                      <TableRow key={hg.id} className='border-b bg-gray-50'>
                        {hg.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={cn(
                              "border-gray-200 px-1 py-2 text-center text-gray-900 sm:px-2 sm:py-3",
                              borderCls,
                            )}
                            style={{ width: header.getSize() }}
                          >
                            {!header.isPlaceholder && (
                              <div
                                className={cn(
                                  "flex items-center justify-center",
                                  header.column.getCanSort() && "cursor-pointer select-none gap-1",
                                )}
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && <ArrowUpDown className='h-3 w-3' />}
                              </div>
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>

                  <TableBody>
                    {table.getRowModel().rows.map((row, i) => {
                      const rd = row.original
                      const isSupervisorReduced = supervisorReducedMap.has(rd?.id)
                      return (
                        <TableRow
                          key={row.id}
                          className={cn(
                            "border-b hover:bg-gray-50",
                            i % 2 === 0 ? "bg-white" : "bg-gray-25",
                            rd.hasChildren && "font-medium",
                            rd?.bill?.amount && "border-y border-primary bg-primary/5",
                            isSupervisorReduced && "bg-amber-50 border-y border-amber-300",
                            declinedItems[rd?.id] && "bg-red-50/50 opacity-60",
                          )}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                "border-gray-200 px-1 py-1.5 sm:px-2 sm:py-2",
                                borderCls,
                              )}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Row count */}
              <div className='flex flex-col items-center justify-between gap-2 py-4 sm:flex-row'>
                <span className='text-xs text-muted-foreground sm:text-sm'>
                  {translate("Showing")} {table.getRowModel().rows.length} {translate("of")}{" "}
                  {hierarchicalData.length} {translate("rows")}
                </span>
              </div>

              {/* Sub-dialogs */}
              <ApproveBoqItems
                showApproveDialog={showApproveDialog}
                setShowApproveDialog={setShowApproveDialog}
                selectedItems={Object.keys(rowSelection)}
                boqItems={boqItems}
                boqId={boqId}
                refetchBoQ={refetch}
                setSelectedItems={handleRowSelection}
              />
              <RequestAdditionalAmount
                boqItem={selectedItemForAdditional}
                setShowAdditionalAmountDialog={setShowAdditionalAmountDialog}
                showAdditionalAmountDialog={showAdditionalAmountDialog}
                refetchBoQ={refetch}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter
          className={cn("flex items-center", isApproval ? "justify-between" : "justify-end")}
        >
          {isApproval && (
            <div className={cn("flex items-center gap-2", isHebrew ? "ml-2" : "")}>
              <Checkbox
                checked={finishApproval}
                onCheckedChange={(v) => setFinishApproval(!!v)}
                id='finish-approval'
              />
              <label
                htmlFor='finish-approval'
                className='cursor-pointer text-xs font-medium sm:text-sm'
              >
                {translate("Finish Approval")}
              </label>
            </div>
          )}
          <div className='flex items-center gap-2'>
            <DialogClose>
              <Button variant='outline'>{translate("Cancel")}</Button>
            </DialogClose>
            <Button
              size='sm'
              variant={isSubmitDisabled ? "secondary" : "default"}
              disabled={isSubmitDisabled}
              onClick={() => setShowConfirmSubmit(true)}
              loading={mutation.isPending}
              className='w-full text-xs sm:w-auto sm:text-sm'
            >
              <SubmitIcon className={cn("h-3 w-3 sm:h-4 sm:w-4", isHebrew ? "ml-1" : "mr-1")} />
              {translate(isApproval ? "Approve bill" : "Edit bill")}
            </Button>
          </div>
        </DialogFooter>

        {/* Confirmation dialog before submit */}
        <Dialog open={showConfirmSubmit} onOpenChange={setShowConfirmSubmit}>
          <DialogContent className='max-w-sm' dir={dir}>
            <DialogHeader>
              <DialogTitle className='text-base'>
                {isHebrew ? "אישור הגשה" : "Confirm Submission"}
              </DialogTitle>
              <DialogDescription className='text-sm'>
                {isHebrew
                  ? `אתה עומד להגיש מחדש ${submissionSummary.itemCount} פריטים, סכום: ${formatCurrency(submissionSummary.totalSum, true)}. להמשיך?`
                  : `You are about to resubmit ${submissionSummary.itemCount} items, total: ${formatCurrency(submissionSummary.totalSum, false)}. Continue?`}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className='flex gap-2'>
              <Button variant='outline' size='sm' onClick={() => setShowConfirmSubmit(false)}>
                {translate("Cancel")}
              </Button>
              <Button
                size='sm'
                onClick={() => {
                  setShowConfirmSubmit(false)
                  mutation.mutate()
                }}
                loading={mutation.isPending}
              >
                {isHebrew ? "אישור" : "Confirm"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}
